import { inject, Injectable } from '@angular/core';
import { PlatformService } from '@services/platform.service';
import { Diagnostic } from '@awesome-cordova-plugins/diagnostic/ngx';
import { NavigatorPermissionResult, NavigatorService } from '@services/navigator.service';

@Injectable({
  providedIn: 'root'
})
export class CallPermissionsService {
  private readonly platformService: PlatformService = inject(PlatformService);
  private readonly diagnostic: Diagnostic = inject(Diagnostic);
  private readonly navigatorService: NavigatorService = inject(NavigatorService);

  public async getMicrophonePermission(): Promise<PermissionState> {
    return (this.platformService.isDevice)
      ? await this.getMicrophonePermissionNative()
      : await this.getMicrophonePermissionWeb();
  }

  private async getMicrophonePermissionNative(): Promise<PermissionState> {
    try {
      const status: string = await this.diagnostic.getMicrophoneAuthorizationStatus();

      switch (status) {
        case this.diagnostic.permissionStatus.GRANTED:
        case this.diagnostic.permissionStatus.GRANTED_WHEN_IN_USE:
          return 'granted';
        case this.diagnostic.permissionStatus.DENIED:
        case this.diagnostic.permissionStatus.DENIED_ALWAYS:
          return 'denied';
        case this.diagnostic.permissionStatus.NOT_REQUESTED:
        case this.diagnostic.permissionStatus.EPHEMERAL:
        default:
          return 'prompt';
      }
    } catch (error) {
      console.error('Native microphone permission check failed', error);
      return 'prompt';
    }
  }

  private async getMicrophonePermissionWeb(): Promise<PermissionState> {
    const permissionResult: NavigatorPermissionResult = await this.navigatorService.getPermissionStatus('microphone');

    if (!permissionResult.supported && (this.platformService.isIos || this.platformService.isSafari)) {
      return 'prompt';
    }
    if (!permissionResult.supported || !permissionResult.status) {
      return 'prompt';
    }

    return permissionResult.status.state;
  }
}
